/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { useHistory } from 'react-router-dom';
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormHelperText } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import { useFormik } from 'formik';
import FormModal from '../../common/modals/FormModal';
import Tooltip from '../../common/Tooltip';
import appTheme from '../../common/theme/appTheme.json';
import { styled } from '@mui/styles';
import MenuItems from '../../common/layout/menu/MenuItems';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';

interface PerformanceOutlookExportModalProps {
  open: boolean;
  onClose: () => void;
  leg?: FlightLegInfo;
  totalLegs: number;
}

enum MissionLegsEnum {
  ALL_LEGS = 'allLegs',
  CURRENT_LEG = 'currentLeg',
}

export enum ContentDisplayEnum {
  STANDARD = 'standard',
  COMPACT = 'compact',
}

export enum ContourDisplayEnum {
  DEFAULT = 'default',
  RL = 'rl',
  FL = 'fl',
}

const FormLabelStyled = styled(FormLabel)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 600,
  fontFamily: appTheme.appFontFamily,
}));

const FormControlLabelStyled = styled(FormControlLabel)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 400,
  fontFamily: appTheme.appFontFamily,
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    fontWeight: 400,
  },
}));

function PerformanceOutlookExportModal({ open, onClose, leg, totalLegs }: PerformanceOutlookExportModalProps) {
  const history = useHistory();
  const formik = useFormik({
    initialValues: {
      missionLegs: MissionLegsEnum.CURRENT_LEG,
      contentDensity: ContentDisplayEnum.STANDARD,
      contourDisplay: ContourDisplayEnum.DEFAULT,
    },
    onSubmit: values => {
      history.push(
        `/${MenuItems.plans}/export?contentDensity=${values.contentDensity}&${
          values.missionLegs === MissionLegsEnum.CURRENT_LEG && leg ? `currentLeg=${leg.id}&` : ''
        }contourDisplay=${values.contourDisplay}`,
      );
    },
  });

  return (
    <FormModal
      open={open}
      onClose={onClose}
      onSubmit={formik.handleSubmit}
      title="Export Flight Plan"
      submitText="Preview"
      closeText="Cancel"
    >
      <Box
        sx={{
          padding: '0 24px 24px 24px',
          display: 'flex',
        }}
      >
        <form
          onReset={formik.handleReset}
          onSubmit={formik.handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FormControl>
            <FormLabelStyled id="mission-legs-radio-group">Mission Legs</FormLabelStyled>
            <RadioGroup
              aria-labelledby="mission-legs-radio-group"
              name="missionLegs"
              value={formik.values.missionLegs}
              onChange={formik.handleChange}
            >
              <FormControlLabelStyled
                value={MissionLegsEnum.CURRENT_LEG}
                control={<Radio size="small" />}
                label={`Current leg only (${leg?.origin ? leg.origin : EMPTY_FIELD_STRING_PLACEHOLDER} - ${
                  leg?.destination ? leg.destination : EMPTY_FIELD_STRING_PLACEHOLDER
                })`}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <FormControlLabelStyled
                  value={MissionLegsEnum.ALL_LEGS}
                  control={<Radio size="small" />}
                  label="Include all legs"
                />
                {formik.values.missionLegs === MissionLegsEnum.ALL_LEGS && totalLegs > 16 && (
                  <FormHelperText
                    sx={{
                      mx: '26px',
                      mt: '0px',
                      display: 'flex',
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontFamily: appTheme.appFontFamily,
                      color: appTheme.mptFormLabelText,
                    }}
                  >
                    <InfoIcon
                      color="primary"
                      sx={{
                        width: '16px',
                        height: '16px',
                        color: appTheme.mptFormLabelText,
                        marginRight: '4px',
                        mt: '2px',
                      }}
                    />
                    Exporting more than 16 legs may result in maps missing in the output file.
                  </FormHelperText>
                )}
              </Box>
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ marginTop: '40px' }}>
            <FormLabelStyled
              id="content-density-radio-group"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Content Density
              <Tooltip
                tooltipMaxWidthPx={350}
                text={
                  <>
                    <div>Standard Format: Map and vertical timeline on separate pages</div>
                    <div>Compact Format: Map and vertical timeline on the same page</div>
                  </>
                }
              >
                <InfoIcon
                  color="primary"
                  sx={{
                    fontSize: '14px',
                    width: '14px',
                    height: '14px',
                    color: appTheme.mptFormLabelText,
                    marginLeft: '6px',
                    display: 'block',
                  }}
                />
              </Tooltip>
            </FormLabelStyled>
            <RadioGroup
              aria-labelledby="content-density-radio-group"
              name="contentDensity"
              value={formik.values.contentDensity}
              onChange={formik.handleChange}
            >
              <FormControlLabelStyled
                value={ContentDisplayEnum.STANDARD}
                control={<Radio size="small" />}
                label="Standard"
              />
              <FormControlLabelStyled
                value={ContentDisplayEnum.COMPACT}
                control={<Radio size="small" />}
                label="Compact"
              />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ marginTop: '40px' }}>
            <FormLabelStyled id="contour-display-radio-group">Contour Display</FormLabelStyled>
            <RadioGroup
              aria-labelledby="contour-display-radio-group"
              name="contourDisplay"
              value={formik.values.contourDisplay}
              onChange={formik.handleChange}
            >
              <FormControlLabelStyled
                value={ContourDisplayEnum.DEFAULT}
                control={<Radio size="small" />}
                label="Default beam contours"
              />
              <FormControlLabelStyled
                value={ContourDisplayEnum.RL}
                control={<Radio size="small" />}
                label="Return link contours"
              />
              <FormControlLabelStyled
                value={ContourDisplayEnum.FL}
                control={<Radio size="small" />}
                label="Forward link contours"
              />
            </RadioGroup>
          </FormControl>
        </form>
      </Box>
    </FormModal>
  );
}

export default PerformanceOutlookExportModal;
